import type { TFunction } from 'i18next';

/**
 * Project media balance information from event.billing.
 */
export interface IProjectMediaBalance {
  currentBalance: number;
  spent: number;
}

/**
 * Validation errors for the media balance form.
 */
export interface IMediaBalanceFormErrors {
  /** Error message for credits input */
  credits?: string;
  /** Error message for project selection */
  project?: string;
}

/**
 * Validates the media balance form inputs.
 *
 * @param creditsToRedeem - The number of credits the user wants to redeem
 * @param availableCredits - The total credits available to the user
 * @param selectedProjectId - The ID of the selected project
 * @param t - Translation function
 * @returns Object containing validation errors, empty if valid
 *
 * @example
 * const errors = validateMediaBalanceForm(100, 500, 'project-1', t);
 * if (Object.keys(errors).length === 0) {
 *   // Form is valid
 * }
 */
export function validateMediaBalanceForm(
  creditsToRedeem: number | undefined,
  availableCredits: number,
  selectedProjectId: string | undefined,
  t: TFunction,
): IMediaBalanceFormErrors {
  const errors: IMediaBalanceFormErrors = {};

  if (!selectedProjectId) {
    errors.project = t('Please select a project');
  }

  if (creditsToRedeem === undefined || creditsToRedeem === null) {
    errors.credits = t('Please enter the number of credits to redeem');
  } else if (creditsToRedeem <= 0) {
    errors.credits = t('Credits must be greater than 0');
  } else if (creditsToRedeem > availableCredits) {
    errors.credits = t('Cannot redeem more credits than available');
  }

  return errors;
}

/**
 * Formats a number of credits with thousand separators.
 *
 * @param credits - Number of credits to format
 * @returns Formatted string with thousand separators
 *
 * @example
 * formatCredits(24550); // Returns "24,550"
 * formatCredits(1000000); // Returns "1,000,000"
 */
export function formatCredits(credits: number): string {
  return credits.toLocaleString();
}

/**
 * Formats a dollar amount as currency.
 *
 * @param amount - Dollar amount to format
 * @returns Formatted currency string
 *
 * @example
 * formatCurrency(245.50); // Returns "$245.50"
 * formatCurrency(1000); // Returns "$1,000.00"
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

/**
 * Gets all validation error messages as an array.
 * Useful for ButtonValid component which expects an array of errors.
 *
 * @param errors - The validation errors object
 * @returns Array of error messages
 *
 * @example
 * const errors = { credits: 'Invalid', project: 'Required' };
 * getValidationErrorArray(errors); // Returns ['Invalid', 'Required']
 */
export function getValidationErrorArray(errors: IMediaBalanceFormErrors): string[] {
  return Object.values(errors).filter((error): error is string => !!error);
}
